import type { PlaneSize } from '@Components/BigBrowse/Model/ModelTypes';
import { BbQueueManager } from '@Components/BigBrowse/bb2/BbQueueManager';
import { calculateLabelAreas } from '@Map/FloorMap/Components/MapPage/MapPageContent/MapLayout/labels';
import { FloorMapPresenter } from '@Map/FloorMap/FloorMapPresenter';
import {
  calculateCellBorders,
  convertInto2DArray,
  getNotchForCell,
} from '@Map/shared/map-cell-helpers';
import type { Border, FloorVM, MapGrid, Notch } from '@Model/MapModelsView';
import { calculateFitScaleFactor } from '@Utils/scale-utils';
import type { TransientClass } from '@Utils/useClassStore';
import { ObservableProp } from '@Utils/useClassStore';
import { inject, injectable } from 'inversify';
import { computed } from 'mobx';
import { createContext, useContext } from 'react';

import container from '~ioc/ioc';
import type { FloorStore } from '~stores/floor';
import { FloorsListStore } from '~stores/floors-list';
import { NavigationStore } from '~stores/navigation';

@injectable()
export class MapPagePresenter implements TransientClass {
  @inject(FloorMapPresenter) private _floorMapPresenter!: FloorMapPresenter;
  @inject(FloorsListStore) private _floorsStore2!: FloorsListStore;
  @inject(NavigationStore) private _navigator2Store!: NavigationStore;
  @inject(BbQueueManager) private _bbQueueManager!: BbQueueManager;

  public floorId = new ObservableProp<FloorVM['id'] | undefined>(undefined);

  init = (floorId: FloorVM['id']) => {
    this.floorId.set(floorId);
  };

  @computed get relativeFloorLevel(): number {
    const selectedFloorId = this._floorMapPresenter.selectedFloorId.value;
    if (!selectedFloorId) {
      return 0;
    }

    const selectedFloorPageIndex = this._floorMapPresenter.floorsVM.findIndex(
      (floor) => floor.id === selectedFloorId
    );

    if (selectedFloorPageIndex === -1) {
      throw new Error(
        `FloorMap Error: Could not find selected floor page index. Selected floor Id: ${selectedFloorId}`
      );
    }

    // the following considers the array to be circular, and returns the distance (always positive)
    // from the SelectedFloorPageIndex to the current floor index. So the page is always displayed
    // below the selected map page.

    if (this.floorLevel < selectedFloorPageIndex) {
      return (
        this._floorMapPresenter.floorsVM.length -
        selectedFloorPageIndex +
        this.floorLevel
      );
    }
    return this.floorLevel - selectedFloorPageIndex;
  }

  @computed get zIndex(): number {
    return 10 - Math.abs(this.relativeFloorLevel) * 10;
  }

  @computed get animatedStyle(): {
    marginTop: number;
    scaleX: number;
  } {
    return {
      marginTop: this.relativeFloorLevel * 5,
      scaleX: 1 - Math.abs(this.relativeFloorLevel) * 0.03,
    };
  }

  @computed get FloorStore(): FloorStore | undefined {
    if (!this.floorId.value) {
      return undefined;
    }

    return this._floorsStore2.getFloorStore(this.floorId.value);
  }

  @computed get floorsDM() {
    return this._floorsStore2.floorsDM;
  }

  @computed get isFetching(): boolean {
    return Boolean(this.FloorStore?.isFetching.value);
  }

  @computed get currentFloor() {
    const floorId = this._bbQueueManager.root?.journeyParams?.journeyParam;
    let result;

    result = this.floorsDM.value.find(
      (item) => item.id === Number(this.floorId.value)
    );

    if (!result) {
      result = this.floorsDM.value.find((item) => item.id === Number(floorId));
    }
    return result;
  }

  @computed get rootFloorId() {
    const floorId = this._bbQueueManager.root?.journeyParams?.journeyParam;
    return Number(floorId);
  }

  @computed private get floorLevel(): number {
    const index = this._floorMapPresenter.floorsVM.findIndex(
      (floor) => floor.id === this.floorId.value
    );

    if (index === -1) {
      throw new Error(
        `FloorMap Error: Could not find pageIndex of floor with Id ${this.floorId.value}`
      );
    }

    return index;
  }

  navToFloor = (floorId: FloorVM['id'], depId?: number) => {
    this._floorMapPresenter.setExtendedMapVisible(false);

    if (floorId !== this.rootFloorId) {
      this._floorMapPresenter.logViewFloorAnalytics();
      this._navigator2Store.navToFloor(floorId, depId);
      return;
    }

    this._floorMapPresenter.logViewDepartmentAnalytics(
      depId!,
      this.currentFloor?.departments.find((dep) => dep.departmentId === depId)
        ?.name ?? ''
    );

    if (this._navigator2Store.currentParams.value?.journeyParam !== floorId) {
      this._navigator2Store.popToRoot();
    }

    this._bbQueueManager.root?.scrollToDepartmentFocusPoint(depId!).then();
  };

  destroy = () => {
    /* do nothing */
  };

  @computed get gridRowCount() {
    return this.currentFloor?.height ?? 0;
  }

  @computed get gridColumnCount() {
    return this.currentFloor?.width ?? 0;
  }

  containerSize = new ObservableProp({ width: 0, height: 0 });

  @computed get scaleAspectRatio(): number {
    return calculateFitScaleFactor({
      containerWidth: this.containerSize.value.width,
      containerHeight: this.containerSize.value.height,
      containedWidth: this.currentFloor?.width ?? 1,
      containedHeight: this.currentFloor?.height ?? 1,
    });
  }
  @computed get scaledDepartmentsBox(): PlaneSize {
    return {
      width: (this.currentFloor?.width ?? 1) * this.scaleAspectRatio,
      height: (this.currentFloor?.height ?? 1) * this.scaleAspectRatio,
    };
  }

  @computed private get gridCells(): MapGrid {
    return convertInto2DArray(this.currentFloor?.departments ?? []);
  }
  @computed get borders(): Border[][][] {
    return calculateCellBorders(this.gridCells);
  }

  @computed get notches(): Notch[][][] {
    return getNotchForCell(this.borders);
  }

  @computed get labelBoxes() {
    const labelAreas = calculateLabelAreas(this.gridCells);

    return labelAreas.map((area) => ({
      ...area,
      label: this.currentFloor?.departments.find(
        (dep) => dep.departmentId === area.departmentId
      )?.name,
    }));
  }

  @computed get floorCameraPosition() {
    return this._bbQueueManager.root?.canvasManager.cameraPosition;
  }

  @computed get floorLayoutSize() {
    return this._bbQueueManager.root?.layout?.size;
  }
}

container.bind(MapPagePresenter).toSelf().inTransientScope();

export const getMapPagePresenter = (
  floorId: FloorVM['id']
): MapPagePresenter => {
  const store = container.get(MapPagePresenter);

  store.init(floorId);
  return store;
};

export const MapPagePresenterContext = createContext<MapPagePresenter | null>(
  null
);

export const useMapPagePresenter = (): MapPagePresenter => {
  const presenter = useContext(MapPagePresenterContext);

  if (!presenter) {
    throw new Error(
      'FloorMap Error: Context value was null for MapPagePresenter'
    );
  }

  return presenter;
};
